import { createAuthClient } from 'better-auth/react';
import { jwtClient } from 'better-auth/client/plugins';

// The SPA talks to the BFF's Better Auth at /bff/auth, SAME-ORIGIN: prod via
// CloudFront, dev via the Vite proxy (see vite.config.ts) — so baseURL is omitted
// (better-auth uses window.location.origin) and only basePath is set, and the
// OAuth callback + session cookies just work. Override with VITE_BFF_BASE only if
// you really want to point the client at a different origin.
const BFF_BASE: string | undefined = import.meta.env.VITE_BFF_BASE;

export const authClient = createAuthClient({
  ...(BFF_BASE ? { baseURL: BFF_BASE } : {}),
  basePath: '/bff/auth',
  // jwtClient adds authClient.token(): the signed JWT we present to /bff/ws as a
  // query param (browsers can't set WS headers) and, later, as a REST bearer.
  plugins: [jwtClient()],
});

export type SessionResult = Awaited<ReturnType<typeof authClient.getSession>>;

// A document navigation, not a router navigate: every bit of in-memory state built on
// the dead session (sockets, cached JWT, cockpit reducer) should go with it.
export function redirectToLogin(): void {
  window.location.assign('/login');
}

// Local-dev fallback. Locally the BFF runs with auth OFF (no Google creds), so
// `/bff/auth/*` 404s and getSession() fails cross-origin ("Failed to fetch") — and
// the route guards would otherwise crash. The BFF deliberately accepts ANONYMOUS
// WebSocket connections in that mode, so here we return a stub session and let the
// cockpit connect tokenless. This kicks in ONLY in dev AND only when auth is
// genuinely unreachable — a reachable auth (real creds) is honoured as-is, incl.
// the logged-out redirect. Set VITE_DISABLE_DEV_AUTH=true to force the real flow.
export const DEV_AUTH_BYPASS =
  import.meta.env.DEV && import.meta.env.VITE_DISABLE_DEV_AUTH !== 'true';

const DEV_SESSION = {
  user: { id: 'dev', email: 'dev@throxy.com', name: 'Dev SDR' },
} as unknown as NonNullable<SessionResult['data']>;

// Local-dev sign-in/out toggle. With auth off locally there's no real session, so
// a localStorage flag drives a working sign-in/out loop: sign-out STICKS (you land
// on /login), and the login screen signs you back in. Default (no flag) = signed
// in, so plain development stays frictionless.
const DEV_SIGNED_OUT_KEY = 'maestro.dev.signed_out';
function devSignedOut(): boolean {
  try {
    return localStorage.getItem(DEV_SIGNED_OUT_KEY) === '1';
  } catch {
    return false;
  }
}
export function devSignIn(): void {
  try {
    localStorage.removeItem(DEV_SIGNED_OUT_KEY);
  } catch {
    /* localStorage unavailable — treat as signed in */
  }
}
export function devSignOut(): void {
  try {
    localStorage.setItem(DEV_SIGNED_OUT_KEY, '1');
  } catch {
    /* localStorage unavailable — best effort */
  }
}

// getSession() with the dev fallback applied. Use this from route guards instead
// of authClient.getSession() directly; production behaviour is unchanged.
export async function getSession(): Promise<SessionResult> {
  if (!DEV_AUTH_BYPASS) return authClient.getSession();
  // Auth is off locally → the dev session, gated by the local signed-out flag.
  const devSession = (): SessionResult =>
    (devSignedOut()
      ? { data: null, error: null }
      : { data: DEV_SESSION, error: null }) as SessionResult;
  try {
    const res = await authClient.getSession();
    // res.error (e.g. 404 — auth off locally) → dev session; a clean response
    // (real auth reachable) is honoured so real dev auth still works.
    if (res.error) return devSession();
    return res;
  } catch {
    return devSession();
  }
}
